import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Empty, Input, Modal, Spin, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import debounce from 'lodash/debounce';

import { ROUTES } from '@config';

import { useGlobalFlowSearchQuery, GlobalSearchResult } from './GlobalSearch.query';
import styles from './GlobalSearch.module.scss';

export const GLOBAL_SEARCH_OPEN_EVENT = 'symmy:global-search:open';

export const openGlobalSearch = () => {
  window.dispatchEvent(new CustomEvent(GLOBAL_SEARCH_OPEN_EVENT));
};

type GlobalSearchProps = {
  open: boolean;
  onClose: () => void;
};

const QUERY_DEBOUNCE_MS = 300;
const MIN_MATCH_LEN = 2;

export const findLongestQueryMatch = (text: string, query: string): string | null => {
  if (!text || !query) return null;
  const t = text.toLowerCase();
  const q = query.toLowerCase();
  const n = t.length;
  const m = q.length;
  let prev = new Array<number>(m + 1).fill(0);
  let bestLen = 0;
  let bestQEnd = 0;
  for (let i = 1; i <= n; i++) {
    const curr = new Array<number>(m + 1).fill(0);
    const ti = t.charCodeAt(i - 1);
    for (let j = 1; j <= m; j++) {
      if (ti === q.charCodeAt(j - 1)) {
        curr[j] = prev[j - 1] + 1;
        if (curr[j] > bestLen) {
          bestLen = curr[j];
          bestQEnd = j;
        }
      }
    }
    prev = curr;
  }
  if (bestLen < MIN_MATCH_LEN) return null;
  return query.slice(bestQEnd - bestLen, bestQEnd);
};

export const renderSnippet = (snippet: string, query: string): ReactNode => {
  if (!snippet) return snippet;
  const match = findLongestQueryMatch(snippet, query);
  if (!match) return snippet;

  const matchLc = match.toLowerCase();
  const snippetLc = snippet.toLowerCase();
  const parts: ReactNode[] = [];
  let pos = 0;
  while (pos < snippet.length) {
    const idx = snippetLc.indexOf(matchLc, pos);
    if (idx === -1) {
      parts.push(<span key={`t-${pos}`}>{snippet.slice(pos)}</span>);
      break;
    }
    if (idx > pos) {
      parts.push(<span key={`t-${pos}`}>{snippet.slice(pos, idx)}</span>);
    }
    parts.push(
      <mark key={`m-${idx}`} className={styles.mark}>
        {snippet.slice(idx, idx + match.length)}
      </mark>,
    );
    pos = idx + match.length;
  }
  return parts;
};

const GlobalSearch = ({ open, onClose }: GlobalSearchProps) => {
  const { _ } = useLingui();
  const navigate = useNavigate();
  const [rawQuery, setRawQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const debouncedSet = useMemo(
    () => debounce((value: string) => setDebouncedQuery(value), QUERY_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    debouncedSet(rawQuery.trim());
    return () => debouncedSet.cancel();
  }, [rawQuery, debouncedSet]);

  useEffect(() => {
    if (!open) {
      setRawQuery('');
      setDebouncedQuery('');
    }
  }, [open]);

  const { data, isFetching, isError } = useGlobalFlowSearchQuery(debouncedQuery);
  const results = data?.results ?? [];

  const goToFlow = (item: GlobalSearchResult) => {
    onClose();
    navigate(
      `${ROUTES.FLOW}/${item.flow_uuid}${ROUTES.FLOW_DETAIL}/${item.mongo_flow_id}`,
      { state: { expandNodeCallUuid: item.matched_node_call_uuid } },
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
      title={<Trans>Search executions</Trans>}
    >
      <Input
        autoFocus
        size="large"
        prefix={<SearchOutlined />}
        allowClear
        value={rawQuery}
        onChange={(e) => setRawQuery(e.target.value)}
        placeholder={_(msg`Search input or output of any execution in this organization…`)}
      />
      <div className={styles.resultsContainer}>
        {isFetching && (
          <div className={styles.statusRow}>
            <Spin size="small" />
          </div>
        )}
        {!isFetching && debouncedQuery.length >= 2 && results.length === 0 && !isError && (
          <Empty description={<Trans>No matching executions</Trans>} />
        )}
        {results.map((item) => (
          <button
            type="button"
            key={item.mongo_flow_id}
            className={styles.resultRow}
            onClick={() => goToFlow(item)}
          >
            <div className={styles.resultHeader}>
              <span className={styles.flowName}>{item.flow_name}</span>
              <Tag>{item.status}</Tag>
              {item.start && <span className={styles.start}>{item.start}</span>}
            </div>
            <div className={styles.snippet}>
              {renderSnippet(item.snippet, debouncedQuery)}
            </div>
          </button>
        ))}
      </div>
    </Modal>
  );
};

export default GlobalSearch;
